import {
    Field,
    Formik,
} from 'formik';
import {I18n} from 'aws-amplify/utils';
import {
    Container,
    Stack,
    Text,
} from '@mantine/core';
import {
    FormikTextInput,
    FormWithButtons,
} from '@flexinet/ui-components';

import {RenameSchema} from './RecordSchema';
import {RenameFormValues} from './types';
import {ProviderConnection} from '../types';
import {productCopy} from '../../../config/productCopy';

// FNP-1304 — THE RENAME FORM. One field, and the copy explains why it is only
// one.
//
// ⚠️ NO CREDENTIAL FIELDS AND NO WRITABLE SWITCH. Those belong to the connect
// and re-validate flows, which re-run validation against the provider. This one
// does not touch the provider at all: `displayName` is the only field on a
// connection that the TENANT asserts, while status, capabilities and
// lastValidatedAt are all records of what the provider said. A rename form
// offering those would be a general edit wearing a rename's name.
//
// ⚠️ THE COPY SAYS WHAT A RENAME DOES NOT DO. A tenant looking at a broken
// connection may reasonably wonder whether renaming re-checks it; a tenant
// looking at a working one may fear renaming breaks it. Both questions have the
// same answer and it is worth putting on screen, because the alternative is
// somebody disconnecting and reconnecting to fix a typo — which is exactly the
// expensive workaround this ticket exists to remove.

export const RenameForm = (
    {
        connection,
        handleSubmit,
        handleCancel,
    }: {
        connection: ProviderConnection,
        handleSubmit: (values: RenameFormValues) => Promise<unknown>,
        handleCancel?: () => void,
    },
) => {

    // Wrapped rather than passed through: FormWithButtons types its cancel prop
    // as a MouseEventHandler, which is not optional.
    const onCancel = () => {
        handleCancel?.();
    };

    // Seeded with the CURRENT name, so the common case — fixing a typo — starts
    // from what is there rather than from an empty box the tenant has to retype.
    const initialValues: RenameFormValues = {
        displayName: connection.displayName,
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={RenameSchema}
            onSubmit={(values: RenameFormValues) => handleSubmit(values)}
        >
            {({
                  handleSubmit: formikHandleSubmit,
                  isSubmitting,
                  ...rest
              }) => (
                <FormWithButtons
                    handleSubmit={formikHandleSubmit}
                    isSubmitting={isSubmitting}
                    handleCancel={onCancel}
                    {...rest}
                >
                    <Container>
                        <Stack gap={'md'}>

                            <Field
                                name={'displayName'}
                                label={I18n.get('Connection name')}
                                description={productCopy('How this connection is labelled in {product}. Your provider never sees it.')}
                                component={FormikTextInput}
                                withAsterisk
                            />

                            <Text
                                size={'xs'}
                                c={'dimmed'}
                            >
                                {I18n.get('Renaming changes the label only. The connection is not re-checked, its status does not change, and your external ID stays the same — so there is nothing to update in your AWS trust policy.')}
                            </Text>

                        </Stack>
                    </Container>
                </FormWithButtons>
            )}
        </Formik>
    );
};
